import './StoryPlayer.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { StoryPlayerProps } from './StoryPlayer.interface';
import { Loader } from '$shared/components';
import { useThumbHash } from '$hooks';

const VideoPlayer: FC<StoryPlayerProps> = (props) => {
  const {
    story,
    muted,
    disableAutoplay = false,
    onPlay,
    onPause,
    onEnd,
    animation,
  } = props;
  const ref = useRef<HTMLVideoElement | null>(null);
  const [isLoading, setLoading] = useState(true);
  const placeholder = useThumbHash(story.thumbHash, story.thumbnail);

  useEffect(() => {
    if (ref.current) {
      ref.current.muted = muted;
    }
  }, [muted, ref.current]);

  const handlePlay = useCallback(() => {
    onPlay && onPlay();
  }, [onPlay]);

  const handlePause = useCallback(() => {
    onPause && onPause();
  }, [onPause]);

  const handleEnd = useCallback(() => {
    onEnd && onEnd();
  }, [onEnd]);

  const handleTimeUpdate = useCallback(() => {
    const video = ref.current;
    if (video) {
      const progress = (video.currentTime / video.duration) * 100;
      animation.start({
        progress,
        config: {
          // duration: 250,
        },
      });
    }
  }, [ref.current]);

  useEffect(() => {
    animation.start({
      progress: 0,
      immediate: true,
    });
  }, [story]);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener('timeupdate', handleTimeUpdate);
      ref.current.addEventListener('play', handlePlay);
      ref.current.addEventListener('pause', handlePause);
      ref.current.addEventListener('ended', handleEnd);

      if (!disableAutoplay) {
        try {
          ref.current.play()?.catch(() => {});
        } catch (e) {}
      }
    }

    return () => {
      if (ref.current) {
        ref.current.removeEventListener('timeupdate', handleTimeUpdate);
        ref.current.removeEventListener('play', handlePlay);
        ref.current.removeEventListener('pause', handlePause);
        ref.current.removeEventListener('ended', handleEnd);
      }
    };
  }, [ref.current, disableAutoplay]);

  useEffect(() => {
    const gotoHandler = (e: any) => {
      const time = e.detail;
      if (ref.current) {
        ref.current.currentTime = time;
      }
    };

    addEventListener('storyGoTo', gotoHandler);

    return () => {
      removeEventListener('storyGoTo', gotoHandler);
    };
  }, []);

  useEffect(() => {
    const playHandler = () => {
      if (ref.current) {
        ref.current.play()?.catch(() => {});
      }
    };
    addEventListener('storyPlay', playHandler);
    return () => removeEventListener('storyPlay', playHandler);
  }, []);

  useEffect(() => {
    const pauseHandler = () => {
      if (ref.current) {
        ref.current.pause();
      }
    };
    addEventListener('storyPause', pauseHandler);
    return () => removeEventListener('storyPause', pauseHandler);
  }, []);

  const handleLoad = useCallback(() => {
    setLoading(false);
  }, []);

  return (
    <div
      className="StoryPlayer__video"
      style={{ backgroundImage: `url(${placeholder})` }}
    >
      <div
        className="StoryPlayer__blur"
        style={{ backgroundImage: `url(${placeholder})` }}
      />
      <video
        src={story.mediaUrl}
        ref={ref}
        controls={false}
        autoPlay
        preload="auto"
        disablePictureInPicture
        playsInline
        poster={placeholder}
        onLoadedData={handleLoad}
      />
      {isLoading && (
        <div className="StoryPlayer__loader">
          <Loader />
        </div>
      )}
    </div>
  );
};

const PhotoPlayer: FC<StoryPlayerProps> = (props) => {
  const { story, disableAutoplay = false, onPlay, onPause, onEnd, animation } = props;
  const ref = useRef<HTMLImageElement>(null);
  const [isLoaded, setLoaded] = useState(false);
  const placeholder = useThumbHash(story.thumbHash, story.thumbnail);
  const [isPlaying, setPlaying] = useState(false);
  const currentTime = useRef(0);
  const [isEnded, setEnded] = useState(false);

  const handleLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded && !disableAutoplay) {
      setPlaying(true);
    }
  }, [isLoaded]);

  useEffect(() => {
    animation.start({
      progress: 0,
      immediate: true,
    });
  }, [story]);

  useEffect(() => {
    const gotoHandler = (e: any) => {
      const time = e.detail;
      currentTime.current = time;
      animation.start({
        progress: time,
        immediate: true,
      });
    };

    addEventListener('storyGoTo', gotoHandler);
    return () => removeEventListener('storyGoTo', gotoHandler);
  }, []);

  useEffect(() => {
    const playHandler = () => {
      setPlaying(true);
    };
    addEventListener('storyPlay', playHandler);
    return () => removeEventListener('storyPlay', playHandler);
  }, []);

  useEffect(() => {
    const pauseHandler = () => {
      setPlaying(false);
    };
    addEventListener('storyPause', pauseHandler);
    return () => removeEventListener('storyPause', pauseHandler);
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      // skip
    } else if (!isPlaying) {
      onPause && onPause();
    } else if (isEnded) {
      onEnd && onEnd();
    } else {
      onPlay && onPlay();
      let lastTick = Date.now();
      const interval = setInterval(() => {
        const now = Date.now();
        currentTime.current += (now - lastTick) / 1000;
        currentTime.current = Math.min(currentTime.current, story.duration);
        lastTick = now;

        const progress = (currentTime.current / story.duration) * 100;
        animation.start({
          progress,
          config: {
            // duration: 250,
          },
        });

        if (progress >= 100) {
          setEnded(true);
        }
      }, 50);

      return () => {
        clearInterval(interval);
      };
    }
  }, [isPlaying, isEnded]);

  return (
    <div className="StoryPlayer__photo">
      <div
        className="StoryPlayer__blur"
        style={{ backgroundImage: `url(${placeholder})` }}
      />
      <img src={story.mediaUrl} ref={ref} onLoad={handleLoad} />
    </div>
  );
};

export const StoryPlayer: FC<StoryPlayerProps> = (props) => {
  const { story } = props;

  if (story.type === 'video') {
    return <VideoPlayer {...props} />;
  }

  return <PhotoPlayer {...props} />;
};
